import React, { FC, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import TextField from '@cld/text-field';
import Checkbox from '@cld/checkbox';
import { Combobox } from '@cld/combobox';
import { InlineFieldLayout, Label } from '@cld/form';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { messages } from '../../../../i18n';
import { FilterByAssetOption, SearchOptions } from '../../../../types';
import { ASSET_TYPES } from '../../../../constants/general';
import { SearchBox, SearchFilterByCustomExpressionBox, SearchFilterByAssetTypeBox } from './OpenOptions.styled';

export interface OpenOptionsSearchProps {
  isSearchFilterByAsset: boolean;
  filterByAsset?: FilterByAssetOption;
  isSearchFilterByCustomExpression: boolean;
  filterByExpression: string;
  onApply(searchOptions: SearchOptions): void;
}

export const OpenOptionsSearch: FC<OpenOptionsSearchProps> = ({
  isSearchFilterByAsset,
  isSearchFilterByCustomExpression,
  filterByAsset = ASSET_TYPES[0],
  filterByExpression,
  onApply,
}) => {
  const analytics = useAnalytics();

  const { formatMessage } = useIntl();
  const [localFilterByExpression, setLocalFilterByExpression] = useState(filterByExpression);

  const handleIsSearchFilterByAsset = (value: boolean) => {
    onApply({
      isSearchFilterByAsset: value,
    });
  };
  const handleIsSearchFilterByCustomExpression = (value: boolean) => {
    onApply({
      isSearchFilterByCustomExpression: value,
    });
  };
  const handleAssetTypeFilter = (value: any) => {
    analytics('edit open console', 'filter by asset type');
    onApply({
      filterByAsset: value as FilterByAssetOption,
    });
  };
  const handleChangeExpression = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      analytics('edit open console', 'filter by custom expression');
      onApply({
        filterByExpression: localFilterByExpression,
      });
    }
  };

  return (
    <SearchBox data-test="search-box">
      <InlineFieldLayout
        label={
          <Label htmlFor="is-search-filter-by-asset">
            <FormattedMessage {...messages['demo.section.filter.filterByAssetType']} />
          </Label>
        }
      >
        <Checkbox id="is-search-filter-by-asset" value={isSearchFilterByAsset} onChange={handleIsSearchFilterByAsset} />
      </InlineFieldLayout>
      {isSearchFilterByAsset && (
        <SearchFilterByAssetTypeBox>
          <Combobox
            size="sm"
            menuPlacement="auto"
            value={filterByAsset}
            options={ASSET_TYPES}
            onChange={handleAssetTypeFilter}
            data-test="asset-type-selector"
          />
        </SearchFilterByAssetTypeBox>
      )}
      <InlineFieldLayout
        label={
          <Label htmlFor="is-search-filter-by-custom-expression">
            <FormattedMessage {...messages['demo.section.filter.filterByCustomExpression']} />
          </Label>
        }
      >
        <Checkbox id="is-search-filter-by-custom-expression" value={isSearchFilterByCustomExpression} onChange={handleIsSearchFilterByCustomExpression} />
      </InlineFieldLayout>
      {isSearchFilterByCustomExpression && (
        <SearchFilterByCustomExpressionBox>
          <TextField
            type="text"
            value={localFilterByExpression}
            onChange={setLocalFilterByExpression}
            inputProps={{ onKeyDown: handleChangeExpression }}
            placeholder={formatMessage(messages['demo.section.filter.filterByCustomExpressionPlaceholder'])}
            data-test="custom-expression"
          />
        </SearchFilterByCustomExpressionBox>
      )}
    </SearchBox>
  );
};
